import React from 'react';
import OdfjellInputComponent, { InputType } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { useAuthStore } from 'src/context/AuthContext';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { BunkeringEvent, BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { BunkerRegInputGrid, BunkerRegInputRow, BunkerRegSectionHeader, InputComponentWrapper } from '../Bunkering.styles';
import { BunkeringInputField, bunkeringInputFields, BunkerRegSectionType } from '../BunkeringUtils';
import { FuelType, getFuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import { prettifyDate, prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { ErrorText } from 'src/components/shared/error-text/ErrorText.style';
import { ListButton } from 'src/styles/button.style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from 'src/components/ui/tooltip/Tooltip';
import { WarningText } from 'src/components/shared/warning-text/WarningText.style';
import { UnitSelection } from 'src/components/aol/aol-container/AolContainer.style';
import { BUNKERING_INPUT_FIELD_KEYS } from 'src/enums/bunkering/bunkeringInputFieldKeys';
import { BiofuelGrade, getBiofuelGrade } from 'src/types/ViewModel/Bunkering/BiofuelGrade';

interface BunkerRegistrationSectionProps {
    title: string;
    section: BunkerRegSectionType;
    selectedBunkeringEvent: BunkeringEvent;
    selectedBunkering: Bunkering;
    debunkeringEvents: BunkeringEvent[];
    handleInputWithChangeObject: (changeObject: any, inputType: InputType) => void;
    removeDebunkering: (debunkeringEvent: BunkeringEvent) => void;
}

const BunkerRegistrationSection: React.FC<BunkerRegistrationSectionProps> = ({
    title,
    section,
    selectedBunkeringEvent,
    selectedBunkering,
    debunkeringEvents,
    handleInputWithChangeObject,
    removeDebunkering,
}) => {
    const { role } = useAuthStore();
    const [selectedUnit, setSelectedUnit] = React.useState<'m3' | 'MT'>('MT');

    const GetFieldOptions = (field: BunkeringInputField) => {
        if (field.key === BUNKERING_INPUT_FIELD_KEYS.FUEL_TYPE && selectedBunkering?.[field.key] === FuelType.HFO) {
            return [getFuelType(FuelType.HFO), getFuelType(FuelType.LSF)];
        }
        return field.options;
    };

    const GetWarningText = (field: BunkeringInputField) => {
        if (
            field.key === BUNKERING_INPUT_FIELD_KEYS.FUEL_TYPE &&
            selectedBunkering?.fuelType === FuelType.LSF &&
            selectedBunkeringEvent?.fuelType === FuelType.HFO
        ) {
            return 'Originally registered as HFO, changed to LSF.';
        } else if (field.key === BUNKERING_INPUT_FIELD_KEYS.FTA_LITER_WEIGHT && selectedBunkering?.literWeightDifference > 5) {
            return `Quantity difference based on BDN liter weight and FTA liter weight is more than 5 MT, please adjust fuel received and ROB values.`;
        }
        return '';
    };

    const getUnitWidthBySectionType = (section: BunkerRegSectionType) => {
        switch (section) {
            case BunkerRegSectionType.FTA:
                return 140;
            case BunkerRegSectionType.OperationalData:
                return 0;
            default:
                return 80;
        }
    };

    const shouldShowEuLcvTooltip =
        selectedBunkering?.biofuelMass &&
        selectedBunkering?.biofuelEnergyContent &&
        selectedBunkering.biofuelMass > 0 &&
        selectedBunkering.biofuelEnergyContent > 0;

    const renderInputComponent = (field: BunkeringInputField) => {
        const inputComponent = (
            <OdfjellInputComponent
                key={`${selectedBunkeringEvent.id}_${field.key}`}
                type={field.inputType as InputType}
                description={field.label}
                name={field.key}
                value={
                    field.key === BUNKERING_INPUT_FIELD_KEYS.FUEL_TYPE ? getFuelType(selectedBunkering?.[field.key]) : selectedBunkering?.[field.key]
                }
                leaveBlank={true}
                onChange={(changeObject) => handleInputWithChangeObject(changeObject, field.inputType as InputType)}
                min={field.min}
                max={
                    field.key === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_MASS && selectedBunkering.totalReceivedQuantity
                        ? Math.min(selectedBunkering.totalReceivedQuantity, field.max)
                        : field.max
                }
                unit={field.unit}
                isReadOnly={
                    !role.isWriter ||
                    (!!selectedBunkering.approvedForUseAt && field.key === BUNKERING_INPUT_FIELD_KEYS.BDN_LITER_WEIGHT) ||
                    (field.key === BUNKERING_INPUT_FIELD_KEYS.FUEL_TYPE && selectedBunkering?.[field.key] !== FuelType.HFO) ||
                    (field.key === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_MASS && selectedUnit === 'm3') ||
                    field.key === BUNKERING_INPUT_FIELD_KEYS.EU_LOWER_CALORIFIC_VALUE
                }
                options={GetFieldOptions(field)}
                selectDefaultOption={field.selectDefaultOption}
                warningText={GetWarningText(field)}
                extraArgs={selectedUnit}
                inputId={field.key}
            />
        );

        // Wrap with tooltip only for EU_LOWER_CALORIFIC_VALUE when conditions are met
        if (field.key === BUNKERING_INPUT_FIELD_KEYS.EU_LOWER_CALORIFIC_VALUE && shouldShowEuLcvTooltip) {
            const calculationText = (
                <>
                    <strong>EU Lower Calorific Value calculation:</strong>
                    <br />
                    <u>{selectedBunkering.biofuelEnergyContent} MJ</u>
                    <br />
                    {selectedBunkering.biofuelMass * 1000} kg
                    <br />= {selectedBunkering.euLowerCalorificValue} MJ/kg
                </>
            );

            return (
                <Tooltip text={calculationText} type="information" disabled={false}>
                    {inputComponent}
                </Tooltip>
            );
        }

        return inputComponent;
    };

    return (
        <div style={{ minHeight: '121px' }}>
            <BunkerRegSectionHeader>
                <h3>{title} </h3>
                <div>
                    {section === BunkerRegSectionType.FTA && (
                        <Tooltip
                            text="Fill in this section when FTA results are available, then the fuel can be approved for use."
                            type="information"
                        >
                            <FontAwesomeIcon title="" icon="info-circle" />
                        </Tooltip>
                    )}
                </div>
            </BunkerRegSectionHeader>

            <BunkerRegInputGrid unitFieldWidth={getUnitWidthBySectionType(section)}>
                {section === BunkerRegSectionType.ProofOfSustainability && (
                    <BunkerRegInputRow>
                        Volume/weight:
                        <UnitSelection>
                            <label>
                                m³
                                <input
                                    type="radio"
                                    value="m3"
                                    name="unitInput"
                                    checked={selectedUnit === 'm3'}
                                    onChange={() => setSelectedUnit('m3')}
                                />
                            </label>
                            <label>
                                MT
                                <input
                                    type="radio"
                                    value="MT"
                                    name="unitInput"
                                    checked={selectedUnit === 'MT'}
                                    onChange={() => setSelectedUnit('MT')}
                                />
                            </label>
                        </UnitSelection>
                    </BunkerRegInputRow>
                )}
                {section === BunkerRegSectionType.OperationalData && (
                    <>
                        <BunkerRegInputRow>
                            Bunkering date/time:
                            <div>{prettifyDateTimeHHmm(selectedBunkeringEvent.bunkeringDateTimeLocal)}</div>
                        </BunkerRegInputRow>
                        <BunkerRegInputRow>
                            Port:
                            <div>{selectedBunkeringEvent.portName}</div>
                        </BunkerRegInputRow>
                    </>
                )}
                {bunkeringInputFields
                    .filter(
                        (f) =>
                            f.section === section &&
                            (selectedBunkeringEvent.fuelType === FuelType.BIO || !f.bioFuel) &&
                            !(f.key === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_VOLUME && selectedUnit === 'MT') &&
                            !(
                                f.key === BUNKERING_INPUT_FIELD_KEYS.BIO_BLEND_FOSSIL_FUEL_TYPE &&
                                selectedBunkering.biofuelGrade === getBiofuelGrade(BiofuelGrade.B100)
                            )
                    )
                    .map((field) => (
                        <InputComponentWrapper
                            key={`${selectedBunkeringEvent.id}_${field.key}_wrapper`}
                            inputFieldWidth={field.key === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_ENERGY_CONTENT ? 120 : null}
                        >
                            {renderInputComponent(field)}
                        </InputComponentWrapper>
                    ))}
                {selectedBunkering?.approvedForUseAt && section === BunkerRegSectionType.BDN && (
                    <BunkerRegInputRow>
                        Approved for use at:
                        <div>{prettifyDate(selectedBunkering.approvedForUseAt)}</div>
                    </BunkerRegInputRow>
                )}
                {debunkeringEvents && section === BunkerRegSectionType.OperationalData && debunkeringEvents.length > 0 && (
                    <>
                        {debunkeringEvents.map((debunkeringEvent) => (
                            <ErrorText key={debunkeringEvent.eventID}>
                                Debunkered at: {prettifyDateTimeHHmm(debunkeringEvent.bunkeringDateTimeLocal)} {debunkeringEvent.portName}
                                <ListButton onClick={() => removeDebunkering(debunkeringEvent)} disabled={!role.isWriter}>
                                    <FontAwesomeIcon title="Disassociate debunkering" icon="times" />
                                </ListButton>
                            </ErrorText>
                        ))}
                        {!(
                            selectedBunkering.currentStatus?.status === BunkeringStatusEnum.Rejected ||
                            selectedBunkering.currentStatus?.status === BunkeringStatusEnum.Debunkered
                        ) && (
                            <WarningText>
                                {`Current status should be "Rejected" if fuel debunkering is in process, or "Debunkered" if debunkering is complete.`}
                            </WarningText>
                        )}
                    </>
                )}
            </BunkerRegInputGrid>
        </div>
    );
};

export default BunkerRegistrationSection;
